/**
 * Template Card Component
 * Displays a template in a visually appealing card format for the gallery
 */

import React from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { formatDistanceToNow } from 'date-fns';
import type { FlowTemplate } from '../../types/flow';

interface TemplateCardProps {
  template: FlowTemplate;
  onLoad: (template: FlowTemplate) => void;
  onRun?: (template: FlowTemplate) => void;
  onDelete?: (template: FlowTemplate) => void;
  showDeleteButton?: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onLoad,
  onRun,
  onDelete,
  showDeleteButton = false
}) => {
  const nodeCount = template.state?.nodes?.length || 0;
  
  // Generate a placeholder gradient based on template name/category
  const getPlaceholderGradient = () => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)'
    ];
    
    // Use template id to consistently pick a gradient
    const hash = template.metadata.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  };

  return (
    <Box
      onClick={() => onLoad(template)}
      sx={{
        backgroundColor: 'var(--node-body-background)',
        border: '1px solid var(--node-border-color)',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'var(--color-accent-primary)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }
      }}
    >
      {/* Thumbnail / Preview Area */}
      <Box
        sx={{
          height: '120px',
          background: template.thumbnail 
            ? `url(${template.thumbnail}) center/cover`
            : getPlaceholderGradient(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        {/* Node count badge */}
        <Box
          sx={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '4px',
            padding: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <AccountTreeIcon sx={{ fontSize: '14px', color: 'white' }} />
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
            {nodeCount} nodes
          </Typography>
        </Box>

        {/* Run button overlay */}
        {onRun && (
          <Tooltip title="Load & Run Template">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onRun(template);
              }}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'var(--color-accent-primary)',
                color: 'white',
                opacity: 0,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--color-accent-primary)',
                  opacity: 1
                },
                '.MuiBox-root:hover &': {
                  opacity: 0.9
                }
              }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Card Content */}
      <Box sx={{ padding: '12px' }}>
        {/* Title */}
        <Typography
          variant="subtitle1"
          sx={{
            color: 'var(--color-text-primary)',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginBottom: '4px'
          }}
        >
          {template.metadata.name}
        </Typography>

        {/* Description */}
        {template.metadata.description && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-secondary)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              marginBottom: '8px',
              fontSize: '12px',
              lineHeight: 1.4
            }}
          >
            {template.metadata.description}
          </Typography>
        )}

        {/* Tags and Category */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          {template.category && (
            <Chip
              label={template.category}
              size="small"
              sx={{
                backgroundColor: 'var(--color-accent-secondary)',
                color: 'white',
                height: '20px',
                fontSize: '10px',
                fontWeight: 500
              }}
            />
          )}
          {template.metadata.tags?.slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                backgroundColor: 'var(--color-bg-control)',
                color: 'var(--color-text-secondary)',
                height: '20px',
                fontSize: '10px'
              }}
            />
          ))}
        </Box>

        {/* Footer: Updated time and usage count */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'var(--color-text-tertiary)' }}
          >
            {formatDistanceToNow(new Date(template.metadata.updatedAt), { addSuffix: true })}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {template.usageCount !== undefined && template.usageCount > 0 && (
              <Typography
                variant="caption"
                sx={{ color: 'var(--color-text-tertiary)' }}
              >
                {template.usageCount} uses
              </Typography>
            )}

            {showDeleteButton && onDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(template);
                }}
                sx={{
                  color: 'var(--color-accent-error)',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.1)'
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateCard;
